import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, shallowEqual } from 'react-redux';
import { Alert, Box, Button, Typography } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EditTimeForm from '../../input/EditTimeForm';
import useOrderTiming from '../../../hooks/useOrderTiming';
import { formatOrderTiming } from '../../../util/orderTiming';
import { opensIn } from '../../../util/Restaurant';
import { getBusinessTimezone } from '../../../util/businessTimezone';
import useMarketplaceTiming from '../../../hooks/useMarketplaceTiming';
import timePickerReadiness from '../../../util/timePickerReadiness';

export default function OrderTimeControl({ showNotice, timeOnly }) {
  const [open, setOpen] = useState(false);
  const anchor = useRef(null);
  const isListing = useMarketplaceTiming();
  const { time, type, details, detailsFailed, detailsLoaded } = useSelector(
    (state) => ({
      time: state.time?.selectedTime,
      type: state.order?.type?.value,
      details: state.restaurant?.selectedRestaurantData,
      detailsFailed: state.restaurant?.restaurantDetailsError,
      detailsLoaded: state.restaurant?.restaurantDetailsLoaded,
    }),
    shallowEqual
  );
  const { requiresConfirmation, expired, unavailable, reviewRequired, key } =
    useOrderTiming();
  let label = formatOrderTiming(
    time,
    type,
    isListing ? undefined : details?.timezone,
    new Date(),
    { includeMode: !timeOnly, includeAsapWindow: timeOnly }
  );
  if (timeOnly) {
    if (!isListing && (time == null || time.type === 'asap')) {
      const schedule = details?.schedules?.[type];
      const readiness = timePickerReadiness({
        details,
        mode: type,
        failed: detailsFailed,
        loaded: detailsLoaded,
      });
      if (readiness !== 'ready') {
        label =
          readiness === 'loading'
            ? 'Loading order times…'
            : 'Order times unavailable';
      } else {
        const available =
          opensIn({
            schedules: schedule,
            date: new Date(Date.now() + (details.delays?.[type] || 0) * 1000),
            timezone: getBusinessTimezone(details.timezone),
          }) === 0;
        if (!available) label = 'Choose an available time';
        else if (time == null) label = 'Loading order times…';
      }
    } else if (time == null) label = 'Loading order times…';
    if (expired) label = `Expired · ${label}`;
  }
  let noticeTitle = 'Order ahead · Confirm your time';
  if (!key) noticeTitle = 'Choose your order time';
  else if (expired) noticeTitle = 'This order time has expired';
  else if (unavailable) noticeTitle = 'Choose an available order time';
  else if (reviewRequired) noticeTitle = 'Review your changed order time';
  let notice =
    'This time was selected automatically. Use Change to choose and accept your order time.';
  if (expired || unavailable) notice = 'Choose an available time to continue.';
  if (reviewRequired)
    notice =
      'Your previous time was replaced. Use Change to review and accept your order time.';
  return (
    <Box sx={{ width: '100%', minWidth: 0 }} data-test-id="order-time-control">
      <Button
        ref={anchor}
        onClick={() => setOpen(true)}
        aria-label={`${label}. Change order time`}
        aria-haspopup="dialog"
        aria-expanded={open}
        data-test-id="change-order-time"
        fullWidth
        sx={{
          minHeight: 48,
          px: 1.5,
          py: 1,
          textTransform: 'none',
          whiteSpace: 'normal',
          justifyContent: 'flex-start',
          textAlign: 'left',
          gap: 1,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: 'background.paper',
          color: 'text.primary',
        }}
      >
        <AccessTimeIcon sx={{ flexShrink: 0 }} />
        <Typography
          component="span"
          sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}
        >
          {label}
        </Typography>
        <Typography
          component="span"
          sx={{ color: 'primary.main', fontWeight: 700, flexShrink: 0 }}
        >
          Change
        </Typography>
      </Button>
      {showNotice &&
        requiresConfirmation &&
        (time?.type !== 'asap' || reviewRequired) && (
          <Alert severity="warning" role="status" sx={{ mt: 1 }}>
            <Typography sx={{ fontWeight: 700 }}>{noticeTitle}</Typography>
            <Typography variant="body2">{notice}</Typography>
          </Alert>
        )}
      <EditTimeForm
        open={open}
        anchorEl={anchor.current}
        onClose={() => setOpen(false)}
        selectedRestaurantDetails={isListing ? null : details}
        allowGeneric={isListing}
      />
    </Box>
  );
}

OrderTimeControl.propTypes = {
  showNotice: PropTypes.bool,
  timeOnly: PropTypes.bool,
};
OrderTimeControl.defaultProps = { showNotice: true, timeOnly: false };
